import { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { useSSOV2Store } from '@hhgtech/hhg-components/ssoV2'
import { useSearchParams } from 'next/navigation'

import { callApiNew } from 'api/axios'
import { PATHS } from 'api/paths'
import { domainLocales } from 'domainLocales'
import { IS_SSOV2_ENABLED, SSO_URL } from 'interfaces/constants'
import { actionTypes } from 'state/actionTypes'
import { AppContext } from 'state/appContext'

import { useRouterExtend } from './useRouterExtend'

export function useBookmark() {
  const { locale, router } = useRouterExtend()
  const searchParams = useSearchParams()
  const {
    state: { userInfo, isLoggedIn },
    dispatch,
  } = useContext(AppContext)
  const { bookmarks = [] } = userInfo || {}
  const [loadingBookmark, setLoadingBookmark] = useState<
    Record<string | number, boolean>
  >({})
  const sourceSSOV2 = useSSOV2Store.getState()?.source

  const onClickBookmark = useCallback(
    (id: number) => {
      return () => {
        if (userInfo?.id) {
          if (bookmarks.includes(id)) {
            setLoadingBookmark((loadingBookmark) => ({
              ...loadingBookmark,
              [id]: true,
            }))
            callApiNew(
              PATHS.SSO.DELETE_BOOKMARK,
              {
                method: 'DELETE',
                data: {
                  host_name: domainLocales[locale],
                  post_ids: [id],
                },
              },
              'sso',
            )
              .then(({ _status }) => {
                if (_status === 1) {
                  dispatch({
                    type: actionTypes.REMOVE_BOOKMARKS,
                    payload: {
                      bookmarks: [id],
                      bookmarkId: id,
                    },
                  })
                }
              })
              .finally(() => {
                setLoadingBookmark((loadingBookmark) => ({
                  ...loadingBookmark,
                  [id]: false,
                }))
              })
          } else {
            setLoadingBookmark((loadingBookmark) => ({
              ...loadingBookmark,
              [id]: true,
            }))
            callApiNew(
              PATHS.SSO.CREATE_BOOKMARK,
              {
                method: 'POST',
                data: {
                  host_name: domainLocales[locale],
                  post_ids: [id],
                },
              },
              'sso',
            )
              .then(({ _status, _data }) => {
                if (_status === 1) {
                  dispatch({
                    type: actionTypes.ADD_BOOKMARKS,
                    payload: {
                      bookmarks: [id],
                      bookmarkId: id,
                      isFirstTimeBookmark: _data.first_time_bookmark,
                    },
                  })
                  router.replace('', '', { shallow: true })
                  useSSOV2Store.setState({ source: undefined })
                }
              })
              .finally(() => {
                setLoadingBookmark((loadingBookmark) => ({
                  ...loadingBookmark,
                  [id]: false,
                }))
              })
          }
        } else {
          const currentLocation = window.location.href
          const returnUrlEncode = encodeURIComponent(
            `${currentLocation}${
              currentLocation.includes('?') ? '&' : '?'
            }source=bookmark,${id}`,
          )
          if (IS_SSOV2_ENABLED) {
            useSSOV2Store.getState().triggerLogin({
              source: `bookmark,${id}`,
            })
          } else {
            window.location.href = SSO_URL
              ? `${SSO_URL}?returnUrl=${returnUrlEncode}`
              : currentLocation
          }
        }
      }
    },
    [bookmarks, loadingBookmark, userInfo?.id, locale],
  )

  useEffect(() => {
    const source = sourceSSOV2 || searchParams.get('source')

    if (
      isLoggedIn &&
      typeof source === 'string' &&
      source?.includes('bookmark')
    ) {
      const articleId = parseInt(source.split(',')?.[1])
      if (bookmarks.some((bm) => bm == articleId)) {
        router.replace('', '', { shallow: true })
        useSSOV2Store.setState({ source: undefined })
        return
      }
      onClickBookmark(articleId)()
    }
  }, [isLoggedIn, bookmarks, sourceSSOV2, searchParams])

  return useMemo(
    () => ({
      loadingBookmark,
      setLoadingBookmark,
      bookmarks,
      onClickBookmark,
      isLoggedIn,
    }),
    [loadingBookmark, bookmarks, isLoggedIn],
  )
}
